import {ModuleManifest, Parameter} from '../../api';

/**
 * Prüfparameter des Qualitätschecks aus dem Modul-Manifest der Workflow API.
 *
 * Die Projekt-Einstellungen (Panel „Prüfeinstellungen") pflegen genau die Werte, die die
 * Qualitätsregeln als Argumente erwarten. Ihre Feldliste kam bisher aus `GET /quality/rules`, also
 * aus der Legacy-Quality-API — wird die abgeschaltet, bliebe das Panel leer. Quelle ist jetzt das
 * Manifest des Moduls `quality_check`, das denselben Katalog trägt.
 *
 * Abgebildet wird auf {@link Parameter}, die Form der Legacy-API: Template und Widgets der
 * Projekt-Einstellungen bleiben damit unverändert. Der Adapter ist die einzige Stelle, die beide
 * Formen kennt.
 */

/** Modul-ID des Qualitätschecks in der Workflow API. */
export const QUALITY_CHECK_MODULE_ID = 'quality_check';

/**
 * Einstellung, die keine Prüfangabe ist: die Regelauswahl gehört in den Regel-Editor, nicht in die
 * Projektstammdaten.
 */
const NON_PARAMETER_KEYS = new Set(['rule_ids', 'rule_id', 'rule', 'rules', 'ruleset_id']);

/** Titel der Regelauswahl („Pruefregel"/„Prüfregel(n)"), falls der Schlüssel abweicht. */
function isRuleSelection(key: string, schema: any): boolean {
  if (NON_PARAMETER_KEYS.has(key)) return true;
  const title = String(schema?.title ?? '').trim().toLowerCase().replace('ü', 'ue');
  return /^pruefregel(n|set)?$/.test(title);
}

/** JSON-Schema-Typ → `Parameter.TypeEnum`. Ein `enum` schlägt den Basistyp. */
function parameterType(schema: any): Parameter.TypeEnum {
  if (Array.isArray(schema?.enum) && schema.enum.length > 0) return Parameter.TypeEnum.ENUM;
  switch (schema?.type) {
    case 'boolean':
      return Parameter.TypeEnum.BOOL;
    case 'integer':
      return Parameter.TypeEnum.INT;
    case 'number':
      return Parameter.TypeEnum.FLOAT;
    default:
      return Parameter.TypeEnum.ENUM;
  }
}

/**
 * Die Prüfparameter eines Modul-Manifests, in der Reihenfolge von `x-order`.
 *
 * Leeres Array, wenn das Modul fehlt oder keine Einstellungen trägt — das Panel zeigt dann nichts,
 * bricht aber nicht ab.
 */
export function qualityParametersFromManifest(modules: ModuleManifest[] | null | undefined): Parameter[] {
  const module = (modules ?? []).find(m => m?.moduleId === QUALITY_CHECK_MODULE_ID);
  const properties = module?.settingsSchema?.['properties'];
  if (!properties || typeof properties !== 'object') return [];

  return Object.entries(properties as Record<string, any>)
    .filter(([key, schema]) => !isRuleSelection(key, schema))
    .sort((a, b) => (a[1]?.['x-order'] ?? 0) - (b[1]?.['x-order'] ?? 0))
    .map(([key, schema], index) => {
      const labels = (schema?.['x-enumLabels'] ?? {}) as Record<string, string>;
      const parameter: Parameter = {
        // Die Legacy-Form trägt eine numerische id, die das Template nicht nutzt — sie dient dort
        // nur der Duplikat-Filterung. Der Index reicht, die Schlüssel sind ohnehin eindeutig.
        id: index,
        value: key,
        name: schema?.title ?? key,
        type: parameterType(schema),
      };
      if (Array.isArray(schema?.enum)) {
        parameter.enums = schema.enum.map((value: any, enumIndex: number) => ({
          id: enumIndex,
          value: String(value),
          name: labels[String(value)] ?? String(value),
        }));
      }
      return parameter;
    });
}
